import React, { useState, useEffect, useCallback } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useIsMobile } from "../../hooks/useResponsive";
import { db } from "../../firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp } from "firebase/firestore";
import { getCurrentPosition, calculateDistance, isWithinRadius, formatDistance, watchPosition } from "../../utils/geofencing";
import { devLog } from "../../utils/devLog";
import { useToast } from "../../components/Toast";
import {
  LuClock,
  LuMapPin,
  LuCircleCheck,
  LuCircleX,
  LuLogIn,
  LuLogOut,
  LuLoader,
  LuTriangleAlert,
  LuHistory,
  LuLocate,
} from "react-icons/lu";

const GEOFENCE_RADIUS = 100; // meters — 100m to account for PC/Wi-Fi GPS inaccuracy

const CheckInOut = ({ userid }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [distance, setDistance] = useState(null);
  const [withinRange, setWithinRange] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [history, setHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [watchId, setWatchId] = useState(null);

  const buildingLat = selectedBuilding?.latitude ? Number(selectedBuilding.latitude) : null;
  const buildingLng = selectedBuilding?.longitude ? Number(selectedBuilding.longitude) : null;
  const hasCoordinates = buildingLat != null && buildingLng != null && !isNaN(buildingLat) && !isNaN(buildingLng);

  // Debug: log building coordinates on load
  useEffect(() => {
    if (selectedBuilding) {
      devLog("[CheckIn] Building:", selectedBuilding.name, "lat:", selectedBuilding.latitude, "lng:", selectedBuilding.longitude, "hasCoords:", hasCoordinates);
    }
  }, [selectedBuilding]);

  const today = new Date().toISOString().split("T")[0];

  // Load today's attendance and history
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedBuilding) {
      loadAttendanceData();
    }
  }, [selectedBuilding]);

  // Start watching position
  useEffect(() => {
    if (!hasCoordinates) return;

    const id = watchPosition(
      (pos) => {
        setUserLocation(pos);
        const dist = calculateDistance(pos.lat, pos.lng, buildingLat, buildingLng);
        setDistance(dist);
        // Account for GPS accuracy: if (distance - accuracy) <= radius, user might be at building
        const effectiveDistance = Math.max(0, dist - (pos.accuracy || 0));
        setWithinRange(effectiveDistance <= GEOFENCE_RADIUS);
        setLocationError(null);
        setLocationLoading(false);
        devLog(`[CheckIn GPS] dist=${Math.round(dist)}m, accuracy=±${Math.round(pos.accuracy||0)}m, effective=${Math.round(effectiveDistance)}m, radius=${GEOFENCE_RADIUS}m, inRange=${effectiveDistance <= GEOFENCE_RADIUS}`);
      },
      (err) => {
        setLocationError(err.message || "Error obteniendo ubicaci\u00F3n");
        setLocationLoading(false);
      }
    );

    setWatchId(id);
    setLocationLoading(true);

    return () => {
      if (id != null) {
        navigator.geolocation.clearWatch(id);
      }
    };
  }, [hasCoordinates, buildingLat, buildingLng]);

  const loadAttendanceData = async () => {
    setLoading(true);
    try {
      const buildingId = Number(selectedBuilding.buildingid);

      // Today's attendance — filtered by this employee
      const employeeId = String(userid);
      const todayQ = query(
        collection(db, "attendance"),
        where("buildingid", "==", buildingId),
        where("employeeid", "==", employeeId),
        where("date", "==", today)
      );
      const todaySnap = await getDocs(todayQ);
      let todayRecord = null;
      todaySnap.forEach((d) => {
        todayRecord = { id: d.id, ...d.data() };
      });
      setTodayAttendance(todayRecord);

      // History (last 30 records) — filtered by this employee
      // No orderBy to avoid requiring composite index; sort client-side
      const historyQ = query(
        collection(db, "attendance"),
        where("buildingid", "==", buildingId),
        where("employeeid", "==", employeeId)
      );
      const historySnap = await getDocs(historyQ);
      const historyData = [];
      historySnap.forEach((d) => {
        historyData.push({ id: d.id, ...d.data() });
      });
      historyData.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setHistory(historyData.slice(0, 30));
    } catch (error) {
      console.error("Error loading attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!withinRange || !userLocation) return;
    setActionLoading(true);
    try {
      const docRef = await addDoc(collection(db, "attendance"), {
        employeeid: String(userid) || "",
        buildingid: Number(selectedBuilding.buildingid),
        checkinTime: Timestamp.now(),
        checkoutTime: null,
        checkinLocation: { lat: userLocation.lat, lng: userLocation.lng },
        checkoutLocation: null,
        date: today,
        status: "checked_in",
      });
      setTodayAttendance({
        id: docRef.id,
        status: "checked_in",
        checkinTime: Timestamp.now(),
        checkoutTime: null,
        checkinLocation: { lat: userLocation.lat, lng: userLocation.lng },
        date: today,
      });
    } catch (error) {
      console.error("Error checking in:", error);
      showToast("Error al marcar entrada. Intentá de nuevo.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!withinRange || !userLocation || !todayAttendance?.id) return;
    setActionLoading(true);
    try {
      await updateDoc(doc(db, "attendance", todayAttendance.id), {
        checkoutTime: Timestamp.now(),
        checkoutLocation: { lat: userLocation.lat, lng: userLocation.lng },
        status: "checked_out",
      });
      setTodayAttendance((prev) => ({
        ...prev,
        status: "checked_out",
        checkoutTime: Timestamp.now(),
        checkoutLocation: { lat: userLocation.lat, lng: userLocation.lng },
      }));
    } catch (error) {
      console.error("Error checking out:", error);
      showToast("Error al marcar salida. Intentá de nuevo.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
  };

  const calculateHours = (checkin, checkout) => {
    if (!checkin || !checkout) return "—";
    const start = checkin?.toDate ? checkin.toDate() : new Date(checkin);
    const end = checkout?.toDate ? checkout.toDate() : new Date(checkout);
    const diff = (end - start) / (1000 * 60 * 60);
    return `${diff.toFixed(1)}h`;
  };

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuTriangleAlert size={48} color="#f59e0b" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
      </div>
    );
  }

  if (!hasCoordinates) {
    return (
      <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>Marcar Asistencia</h1>
        </div>
        <div style={styles.warningCard}>
          <LuTriangleAlert size={32} color="#f59e0b" />
          <h3 style={{ margin: "12px 0 4px", color: "#92400e" }}>Ubicaci\u00F3n del edificio no configurada</h3>
          <p style={{ color: "#a16207", fontSize: 14, margin: 0 }}>
            El administrador debe configurar las coordenadas GPS del edificio para habilitar la asistencia.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Marcar Asistencia</h1>
        <p style={styles.pageSubtitle}>{selectedBuilding.name} &middot; {new Date().toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>

      {/* Location Status */}
      <div style={{
        ...styles.locationCard,
        borderColor: locationError ? "#fecaca" : withinRange ? "#bbf7d0" : "#fed7aa",
        backgroundColor: locationError ? "#fef2f2" : withinRange ? "#f0fdf4" : "#fffbeb",
      }}>
        <div style={styles.locationHeader}>
          <LuLocate size={16} color={locationError ? "#dc2626" : withinRange ? "#16a34a" : "#d97706"} />
          <span style={{
            fontSize: 13,
            fontWeight: 400,
            color: locationError ? "#dc2626" : withinRange ? "#16a34a" : "#d97706",
          }}>
            {locationLoading ? "Obteniendo ubicaci\u00F3n..." : locationError ? "Error de GPS" : withinRange ? "Dentro del rango" : "Fuera del rango"}
          </span>
        </div>
        {locationError ? (
          <p style={{ fontSize: 13, color: "#dc2626", margin: "8px 0 0" }}>{locationError}</p>
        ) : distance != null ? (
          <div style={styles.locationDetails}>
            <div style={styles.locationDetail}>
              <LuMapPin size={12} color="#6b7280" />
              <span style={{ fontSize: 13, color: "#374151" }}>Distancia al edificio: <strong>{formatDistance(distance)}</strong></span>
            </div>
            <div style={styles.locationDetail}>
              <LuClock size={12} color="#6b7280" />
              <span style={{ fontSize: 13, color: "#374151" }}>Radio requerido: <strong>{GEOFENCE_RADIUS}m</strong></span>
            </div>
            {userLocation?.accuracy && (
              <div style={styles.locationDetail}>
                <LuLocate size={12} color="#6b7280" />
                <span style={{ fontSize: 13, color: "#374151" }}>Precisi\u00F3n GPS: <strong>\u00B1{Math.round(userLocation.accuracy)}m</strong></span>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Action Card */}
      <div style={styles.actionCard}>
        {loading ? (
          <div style={styles.loadingCenter}>
            <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
          </div>
        ) : !todayAttendance ? (
          // No attendance today - show Check In
          <div style={styles.actionContent}>
            <div style={styles.actionIconWrapper}>
              <LuLogIn size={32} color={withinRange ? "#10b981" : "#9ca3af"} />
            </div>
            <h2 style={styles.actionTitle}>Marcar Entrada</h2>
            <p style={styles.actionDescription}>
              {withinRange
                ? "Est\u00E1s dentro del rango. Pod\u00E9s marcar tu entrada."
                : `Deb\u00E9s estar a menos de ${GEOFENCE_RADIUS}m del edificio para fichar.`}
            </p>
            <button
              style={{
                ...styles.actionButton,
                backgroundColor: withinRange && !actionLoading ? "#10b981" : "#d1d5db",
                cursor: withinRange && !actionLoading ? "pointer" : "not-allowed",
              }}
              onClick={handleCheckIn}
              disabled={!withinRange || actionLoading}
            >
              {actionLoading ? (
                <LuLoader size={16} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <LuLogIn size={16} />
              )}
              Marcar Entrada
            </button>
          </div>
        ) : todayAttendance.status === "checked_in" ? (
          // Checked in - show Check Out
          <div style={styles.actionContent}>
            <div style={styles.actionIconWrapper}>
              <LuLogOut size={32} color={withinRange ? "#ef4444" : "#9ca3af"} />
            </div>
            <h2 style={styles.actionTitle}>Marcar Salida</h2>
            <p style={styles.actionDescription}>
              Entrada registrada a las {formatTime(todayAttendance.checkinTime)}.
              {withinRange
                ? " Pod\u00E9s marcar tu salida."
                : ` Deb\u00E9s estar a menos de ${GEOFENCE_RADIUS}m del edificio.`}
            </p>
            <button
              style={{
                ...styles.actionButton,
                backgroundColor: withinRange && !actionLoading ? "#ef4444" : "#d1d5db",
                cursor: withinRange && !actionLoading ? "pointer" : "not-allowed",
              }}
              onClick={handleCheckOut}
              disabled={!withinRange || actionLoading}
            >
              {actionLoading ? (
                <LuLoader size={16} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <LuLogOut size={16} />
              )}
              Marcar Salida
            </button>
          </div>
        ) : (
          // Already checked out
          <div style={styles.actionContent}>
            <div style={styles.actionIconWrapper}>
              <LuCircleCheck size={32} color="#10b981" />
            </div>
            <h2 style={styles.actionTitle}>Jornada Completada</h2>
            <p style={styles.actionDescription}>
              Entrada: {formatTime(todayAttendance.checkinTime)} &middot; Salida: {formatTime(todayAttendance.checkoutTime)}
              <br />
              Horas: {calculateHours(todayAttendance.checkinTime, todayAttendance.checkoutTime)}
            </p>
          </div>
        )}
      </div>

      {/* History */}
      <div style={styles.historySection}>
        <div style={styles.historyHeader}>
          <LuHistory size={16} color="#6b7280" />
          <h2 style={styles.historySectionTitle}>Historial de Asistencia</h2>
        </div>

        {history.length === 0 ? (
          <p style={styles.emptyHistory}>No hay registros de asistencia a\u00FAn.</p>
        ) : (
          <div style={styles.historyList}>
            {history.map((record) => (
              <div key={record.id} style={styles.historyItem}>
                <div style={styles.historyDate}>
                  {new Date(record.date + "T12:00:00").toLocaleDateString("es-UY", { weekday: "short", day: "numeric", month: "short" })}
                </div>
                <div style={styles.historyTimes}>
                  <span style={styles.historyTime}>
                    <LuLogIn size={10} color="#10b981" /> {formatTime(record.checkinTime)}
                  </span>
                  <span style={styles.historyTime}>
                    <LuLogOut size={10} color="#ef4444" /> {formatTime(record.checkoutTime)}
                  </span>
                </div>
                <div style={styles.historyHours}>
                  {calculateHours(record.checkinTime, record.checkoutTime)}
                </div>
                <div style={{
                  ...styles.historyStatus,
                  backgroundColor: record.status === "checked_out" ? "#ecfdf5" : "#fffbeb",
                  color: record.status === "checked_out" ? "#16a34a" : "#d97706",
                }}>
                  {record.status === "checked_out" ? "Completado" : "En curso"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: 24,
    maxWidth: 800,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#1a1a1a",
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 400,
    color: "#1a1a1a",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#6b7280",
    margin: "4px 0 0",
    textTransform: "capitalize",
  },
  warningCard: {
    backgroundColor: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 12,
    padding: 24,
    textAlign: "center",
  },
  locationCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    border: "1px solid",
  },
  locationHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  locationDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 10,
  },
  locationDetail: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  actionCard: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 32,
    marginBottom: 24,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb",
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: 24,
  },
  actionContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  actionIconWrapper: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    backgroundColor: "#f9fafb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  actionTitle: {
    fontSize: 22,
    fontWeight: 400,
    color: "#1a1a1a",
    margin: "0 0 8px",
  },
  actionDescription: {
    fontSize: 14,
    color: "#6b7280",
    margin: "0 0 20px",
    lineHeight: 1.5,
  },
  actionButton: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 32px",
    color: "#ffffff",
    border: "none",
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    transition: "all 0.15s ease",
  },
  historySection: {
    marginTop: 8,
  },
  historyHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  historySectionTitle: {
    fontSize: 18,
    fontWeight: 400,
    color: "#1a1a1a",
    margin: 0,
  },
  emptyHistory: {
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 14,
    padding: 24,
  },
  historyList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  historyItem: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    backgroundColor: "#ffffff",
    borderRadius: 10,
    padding: "12px 16px",
    border: "1px solid #e5e7eb",
  },
  historyDate: {
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
    minWidth: 90,
    textTransform: "capitalize",
  },
  historyTimes: {
    display: "flex",
    gap: 16,
    flex: 1,
  },
  historyTime: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    color: "#6b7280",
  },
  historyHours: {
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
    minWidth: 50,
    textAlign: "right",
  },
  historyStatus: {
    fontSize: 11,
    fontWeight: 400,
    padding: "4px 10px",
    borderRadius: 20,
  },
};

export default CheckInOut;
